import { findFirstNonEmptyString, PageType } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { EntityLink } from "scmp-app/components/entity-link";
import { Button } from "scmp-app/components/posties/button";
import { OvalBottomBackground } from "scmp-app/components/posties/content-homepage/oval-background";
import { tracking } from "scmp-app/data";
import type { yourSayQuery$key } from "scmp-app/queries/__generated__/yourSayQuery.graphql";

import {
  Container,
  MessageContainer,
  StyledArticleHeadline,
  StyledContentCoverImage,
  StyledGrass,
  StyledHeadline,
  StyledOffsetContainer,
  StyledYourSayAuthor,
  YourSayAuthors,
} from "./styles";

type Props = {
  className?: string;
  reference: yourSayQuery$key;
};

export const YourSay: FunctionComponent<Props> = ({ className, reference: reference_ }) => {
  const data = useFragment(
    graphql`
      fragment yourSayQuery on Query {
        yourSaySection: section(filter: { entityId: "515466" }) {
          ...headlineSection
        }
        yourSayQueue: queue(filter: { name: "section_top_515466" }) {
          items(first: 1) {
            edges {
              node {
                ... on Article {
                  ...entityLink
                  headline
                  ...contentCoverImageContent
                    @arguments(withSize1200x800: true, withSize540x360: true)
                  socialHeadline
                  authors {
                    ...yourSayAuthorAuthor
                  }
                }
              }
            }
          }
        }
      }
    `,
    reference_,
  );

  const yourSayNode = data?.yourSayQueue?.items?.edges[0]?.node;

  const renderHeadline = findFirstNonEmptyString(
    yourSayNode?.socialHeadline,
    yourSayNode?.headline,
  );

  const customQueryParameters = {
    module: tracking.module.YourSay,
    pgtype: tracking.pageType.Content,
  };

  const firstAuthor = first(yourSayNode?.authors ?? []);

  return (
    <Container className={className}>
      <StyledHeadline
        color="#0C635B"
        query={{
          pageType: PageType.PostiesSection,
          ...customQueryParameters,
        }}
        reference={data.yourSaySection}
      />
      {yourSayNode && (
        <MessageContainer>
          <EntityLink
            query={{
              pageType: PageType.PostiesArticle,
              ...customQueryParameters,
            }}
            reference={yourSayNode}
          >
            <StyledOffsetContainer backgroundColor="#F8FFAC" borderRadius="40px" offset={10}>
              <StyledContentCoverImage
                reference={yourSayNode}
                responsiveVariants={{
                  desktopUp: "size1200x800",
                  mobileUp: "size540x360",
                  tabletUp: "size540x360",
                }}
              />
              <StyledArticleHeadline>{renderHeadline}</StyledArticleHeadline>
              <Button variant="button">READ MORE</Button>
            </StyledOffsetContainer>
          </EntityLink>
        </MessageContainer>
      )}
      {firstAuthor && (
        <YourSayAuthors>
          <StyledYourSayAuthor reference={firstAuthor} />
        </YourSayAuthors>
      )}
      <StyledGrass />
      <OvalBottomBackground $ovalFillColor="transparent" $rectFillColor="#0F75BF" />
    </Container>
  );
};

YourSay.displayName = "YourSay";
